import React, { useState, useEffect } from 'react';
import { Download, Trash2, Loader2, Send, Plus, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { RichTextEditor } from '../../components/RichTextEditor';
import { api, API } from '../../lib/api';
import { formatDate, NEWSLETTER_TOPICS } from '../../components/Shared';
import { toast } from 'sonner';

const EMPTY_BROADCAST = { subject: '', body_html: '', type: 'newsletter', template: 'standard', audience_topics: ['general'] };

export default function AdminNewsletter() {
  const [tab, setTab] = useState('subscribers');
  const [subs, setSubs] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [provider, setProvider] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BROADCAST);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/admin/subscribers').then((r) => setSubs(r.data || []));
    api.get('/admin/broadcasts').then((r) => setBroadcasts(r.data || []));
    api.get('/admin/settings').then((r) => setProvider(r.data?.newsletter_provider || {}));
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/subscribers/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ward8_subscribers.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const saveBroadcast = async () => {
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/admin/broadcasts/${editing.id}`, form);
      else await api.post('/admin/broadcasts', form);
      toast.success('Draft saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const testSend = async (b) => {
    if (!testEmail) { toast.error('Enter a test email address first'); return; }
    try {
      const res = await api.post(`/admin/broadcasts/${b.id}/test-send`, { test_email: testEmail });
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    }
  };

  const sendBroadcast = async (b) => {
    if (!window.confirm(`Send "${b.subject || '(no subject)'}" to all matching subscribers now? Real emails will be delivered.`)) return;
    try {
      const res = await api.post(`/admin/broadcasts/${b.id}/send`);
      toast.success(res.data.message);
      setTimeout(load, 4000);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
  };

  const saveProvider = async () => {
    await api.put('/settings', { key: 'newsletter_provider', value: provider });
    toast.success('Provider settings saved');
  };

  return (
    <div data-testid="admin-newsletter">
      <h1 className="font-serif font-black text-2xl text-[#1a2744] mb-1">Newsletter & Alerts</h1>
      <p className="text-sm text-slate-500 mb-4" data-testid="newsletter-status-line">Subscriber list, broadcast drafts, and delivery settings.{' '}
        {(provider.provider || 'none') !== 'none' && provider.sending_active
          ? <strong className="text-emerald-700">Email delivery is LIVE via campaign SMTP (info@stevelesperance.com) — sends deliver real emails.</strong>
          : <strong>No email provider is connected yet — sends are recorded as placeholders.</strong>}
      </p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-12 p-1 flex-wrap h-auto justify-start">
          <TabsTrigger value="subscribers" className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="newsletter-tab-subscribers">Subscribers ({subs.length})</TabsTrigger>
          <TabsTrigger value="broadcasts" className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="newsletter-tab-broadcasts">Broadcasts</TabsTrigger>
          <TabsTrigger value="settings" className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="newsletter-tab-settings">Provider Settings</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'subscribers' && (
        <div className="mt-5">
          <div className="flex justify-end mb-3">
            <Button onClick={exportCsv} variant="outline" className="h-10 font-bold btn-press" data-testid="newsletter-export-csv"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead className="bg-slate-50 border-b"><tr>
                <th className="text-left font-bold text-slate-600 px-4 py-3">Email</th>
                <th className="text-left font-bold text-slate-600 px-4 py-3">Name</th>
                <th className="text-left font-bold text-slate-600 px-4 py-3">Topics</th>
                <th className="text-left font-bold text-slate-600 px-4 py-3">Subscribed</th>
                <th className="px-4 py-3" />
              </tr></thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100" data-testid="subscriber-row">
                    <td className="px-4 py-3">{s.email}</td>
                    <td className="px-4 py-3">{s.name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{(s.topics || []).join(', ')}</td>
                    <td className="px-4 py-3 text-xs">{formatDate(s.created_at)}</td>
                    <td className="px-4 py-3 text-right"><button onClick={async () => { await api.delete(`/admin/subscribers/${s.id}`); load(); }} className="text-red-600 p-1.5 rounded hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No subscribers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'broadcasts' && (
        <div className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="Test email address" className="h-10 bg-white max-w-xs" data-testid="newsletter-test-email" />
            <Button onClick={() => { setForm(EMPTY_BROADCAST); setEditing({}); }} className="h-10 bg-[#1a2744] text-white font-bold btn-press" data-testid="broadcast-add-button"><Plus className="h-4 w-4 mr-1" /> New Draft</Button>
          </div>
          <div className="space-y-3">
            {broadcasts.map((b) => (
              <Card key={b.id} data-testid="broadcast-row">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="min-w-0">
                      <p className="font-bold text-[#1a2744] truncate">{b.subject || '(no subject)'}</p>
                      <p className="text-xs text-slate-500">{b.type} · {b.status} · topics: {(b.audience_topics || []).join(', ')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="font-bold" onClick={() => { setForm({ ...EMPTY_BROADCAST, ...b }); setEditing(b); }}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                      <Button variant="outline" size="sm" className="font-bold" onClick={() => testSend(b)} data-testid="broadcast-test-send"><Send className="h-3.5 w-3.5 mr-1" /> Test Send</Button>
                      <Button size="sm" className="bg-[#C9A84C] text-[#1a2744] font-bold hover:bg-[#b8973f]" onClick={() => sendBroadcast(b)} data-testid="broadcast-send">Send</Button>
                      <button onClick={async () => { await api.delete(`/admin/broadcasts/${b.id}`); load(); }} className="text-red-600 p-1.5 rounded hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  {b.send_history?.length > 0 && (
                    <div className="mt-3 border-t pt-2">
                      <p className="text-xs font-bold text-slate-500 mb-1">Send history</p>
                      {b.send_history.slice(-3).reverse().map((h) => (
                        <p key={h.id} className="text-xs text-slate-500">{formatDate(h.date)} · {h.type} {h.to ? `→ ${h.to}` : `→ ${h.audience_count} subscriber(s)`} · <span className="text-amber-700 font-semibold">{h.result}</span></p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {broadcasts.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No broadcast drafts yet.</CardContent></Card>}
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <Card className="mt-5"><CardContent className="p-5 space-y-4">
          {(provider.provider || 'none') !== 'none' && provider.sending_active ? (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="provider-live-banner">Email delivery is <strong>LIVE</strong> via the campaign mailbox <strong>info@stevelesperance.com</strong> (SMTP: {provider.smtp_host || 'mail.stevelesperance.com'}). Broadcast sends and test sends deliver real emails with secure unsubscribe links. Credentials are stored securely on the server — never in this form.</p>
          ) : (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">Email sending is not connected yet. Fill in a provider below when ready — the campaign developer can then wire real sending without rebuilding anything.</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Provider</label>
              <Select value={provider.provider || 'none'} onValueChange={(v) => setProvider((p) => ({ ...p, provider: v }))}>
                <SelectTrigger className="h-11" data-testid="provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (placeholder mode)</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="mailchimp">Mailchimp</SelectItem>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="emergent">Emergent Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Sender email</label>
              <Input value={provider.sender_email || ''} onChange={(e) => setProvider((p) => ({ ...p, sender_email: e.target.value }))} className="h-11" data-testid="provider-sender-email" />
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Reply-to</label>
              <Input value={provider.reply_to || ''} onChange={(e) => setProvider((p) => ({ ...p, reply_to: e.target.value }))} className="h-11" />
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">SendGrid API key</label>
              <Input type="password" value={provider.sendgrid_api_key || ''} onChange={(e) => setProvider((p) => ({ ...p, sendgrid_api_key: e.target.value }))} className="h-11" />
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Mailchimp API key</label>
              <Input type="password" value={provider.mailchimp_api_key || ''} onChange={(e) => setProvider((p) => ({ ...p, mailchimp_api_key: e.target.value }))} className="h-11" />
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">SMTP host</label>
              <Input value={provider.smtp_host || ''} onChange={(e) => setProvider((p) => ({ ...p, smtp_host: e.target.value }))} className="h-11" />
            </div>
          </div>
          <Button onClick={saveProvider} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="provider-save-button">Save Settings</Button>
        </CardContent></Card>
      )}

      {/* Broadcast editor */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="broadcast-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{editing?.id ? 'Edit Broadcast' : 'New Broadcast Draft'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Subject</label>
              <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="h-11" data-testid="broadcast-subject-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newsletter">Newsletter</SelectItem>
                    <SelectItem value="alert">Alert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Template</label>
                <Select value={form.template} onValueChange={(v) => setForm((f) => ({ ...f, template: v }))}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard newsletter</SelectItem>
                    <SelectItem value="alert">Urgent alert</SelectItem>
                    <SelectItem value="report">Monthly report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Audience topics</label>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {NEWSLETTER_TOPICS.map((t) => (
                  <label key={t.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox checked={(form.audience_topics || []).includes(t.value)} onCheckedChange={() => setForm((f) => ({ ...f, audience_topics: (f.audience_topics || []).includes(t.value) ? f.audience_topics.filter((x) => x !== t.value) : [...(f.audience_topics || []), t.value] }))} />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Content</label>
              <RichTextEditor value={form.body_html} onChange={(v) => setForm((f) => ({ ...f, body_html: v }))} />
            </div>
            <div className="flex gap-3">
              <Button onClick={saveBroadcast} disabled={busy} className="flex-1 h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="broadcast-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Draft</Button>
              <Button variant="outline" onClick={() => setEditing(null)} className="h-11 font-bold">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
